import { Group, Mesh, type Material } from 'three/webgpu'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'

export type CargoPiece = 'forklift' | 'pallet_soda' | 'pallet_paper' | 'sign_supply' | 'sign_camp' | 'camp_tent'
const PIECES: readonly CargoPiece[] = ['forklift', 'pallet_soda', 'pallet_paper', 'sign_supply', 'sign_camp', 'camp_tent']

/** Blender beach props as templates: Y-up, +Z forward (the forks' direction). */
export interface LandingCargo {
  /** A fresh copy sharing the template's GPU resources. */
  clone(piece: CargoPiece): Group
  dispose(): void
}

export async function loadLandingCargo(): Promise<LandingCargo> {
  const { scene } = await new GLTFLoader().loadAsync('/models/landing/landing-cargo.glb')
  // Same Blender +X-forward to game +Z-forward turn as the boats.
  const frame = new Group()
  frame.rotation.y = -Math.PI / 2
  frame.add(scene)
  frame.updateMatrixWorld(true)
  const templates = new Map<CargoPiece, Group>()
  for (const name of PIECES) {
    const node = scene.getObjectByName(name)
    if (!node) throw new Error(`Missing landing cargo piece: ${name}`)
    const template = new Group()
    template.name = name
    template.attach(node)
    template.traverse(object => {
      if (!(object as Mesh).isMesh) return
      object.castShadow = true
      object.receiveShadow = true
    })
    templates.set(name, template)
  }
  return {
    clone: piece => templates.get(piece)!.clone(true),
    dispose() {
      const materials = new Set<Material>()
      for (const template of templates.values()) template.traverse(object => {
        const mesh = object as Mesh
        if (!mesh.isMesh) return
        mesh.geometry.dispose()
        for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) materials.add(material)
      })
      for (const material of materials) material.dispose()
    },
  }
}
